import { useMemo, useState } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import Select from "@/v2-ui/select"
import Separator from "@/v2-ui/separator"
import Tooltip from "@/v2-ui/tooltip"
import Badge from "@/v2-backoffice-client/components/badge"
import Table, {
  createColumns,
  type TableProps,
  useTableSortUrl,
} from "@/v2-backoffice-client/components/table"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import { buildCustomerViewLink } from "@/v2-backoffice-client/customer/customer.links"
import LicenseGoverningBadge from "@/v2-backoffice-client/license/LicenseGoverningBadge"
import { useLicenseGoverningStatuses } from "@/v2-backoffice-client/license/license.statuses.hooks"
import { LICENSE_STATUS_NONE } from "@/v2-backoffice-client/license/license.constants"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import UsageReportKpi from "@/v2-backoffice-client/usage/report/UsageReportKpi"
import { usageKpiGridClass } from "@/v2-backoffice-client/usage/report/usage.report.utils"
import {
  APP_CODE_FILTER_OPTIONS,
  formatAppCode,
  getAppCodeColor,
} from "@/v2-backoffice-client/usage/usage.constants"
import {
  formatLicenseDeniedReason,
  LICENSE_DENIED_FEED_WINDOW_OPTIONS,
  type LicenseDeniedFeedWindow,
} from "@/v2-backoffice-client/usage/denied/usage.denied.constants"
import { useUsageLicenseDeniedFeedQuery } from "@/v2-backoffice-client/usage/denied/usage.deniedFeed.query"
import type { UsageLicenseDeniedFeedQuery } from "@/v2-backoffice-client/usage/denied/__types__/usage.deniedFeed.query"

type UsageLicenseDeniedEvent =
  UsageLicenseDeniedFeedQuery["getUsageLicenseDeniedFeed"][number]

// One feed row = one stored refusal row, plus the display names the columns
// sort and search on.
export type UsageLicenseDeniedFeedRow = UsageLicenseDeniedEvent & {
  Id: string
  UserName: string
  ReasonLabel: string
  FeatureLabel: string
}

const COLUMN_SIZING = {
  Date: 110,
  CustomerName: 200,
  UserName: 190,
  AppCode: 130,
  FeatureLabel: 230,
  ReasonLabel: 210,
  Attempts: 90,
  LicenseStatusCode: 190,
}

// Filtering is client-side (the query loads the whole window, capped at
// 5 000 rows by the proc), so the filter configs run through TanStack's
// filterFns against getValue.
const COLUMNS = createColumns<UsageLicenseDeniedFeedRow>({
  enableSorting: true,
  enableHiding: true,
  columns: {
    Date: {
      id: "Date",
      label: "Day",
      description:
        "The UTC day the attempts were counted on. Rows arrive the morning after.",
      getValue: (row) => row.Date,
      filter: { type: "date-range" },
      renderCell({ row }) {
        return (
          <span className="tabular-nums">
            {formatDateString(row.original.Date)}
          </span>
        )
      },
      exportValue({ row }) {
        return formatDateString(row.original.Date)
      },
    },
    CustomerName: {
      id: "CustomerName",
      label: "Customer",
      to: ({ row }) =>
        row.original.ExternalCustomerId
          ? buildCustomerViewLink(row.original.ExternalCustomerId, "sign-ins")
          : undefined,
      getValue: (row) => row.CustomerName,
      enableGrouping: true,
      filter: { type: "text" },
    },
    UserName: {
      id: "UserName",
      label: "Person",
      to: ({ row }) => `?v=user:${row.original.UserId}`,
      getValue: (row) => row.UserName,
      enableGrouping: true,
      filter: { type: "text" },
      renderCell({ row }) {
        return (
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="truncate">{row.original.UserName}</span>
            {row.original.Email &&
              row.original.Email !== row.original.UserName && (
                <span className="truncate text-xs text-muted-foreground">
                  {row.original.Email}
                </span>
              )}
          </span>
        )
      },
    },
    AppCode: {
      id: "AppCode",
      label: "Product",
      description: "Where the refusal happened: the console or an MCP server.",
      getValue: (row) => row.AppCode,
      enableGrouping: true,
      renderGroupLabel: (value) => formatAppCode(String(value)),
      filter: {
        type: "multi-select",
        placeholder: "Any product",
        toolbar: true,
        options: APP_CODE_FILTER_OPTIONS,
      },
      renderCell({ row }) {
        return (
          <Badge variant="outline" size="xs">
            <span
              className="mr-1 inline-block size-2 rounded-full"
              style={{ backgroundColor: getAppCodeColor(row.original.AppCode) }}
            />
            {formatAppCode(row.original.AppCode)}
          </Badge>
        )
      },
      exportValue({ row }) {
        return formatAppCode(row.original.AppCode)
      },
    },
    FeatureLabel: {
      id: "FeatureLabel",
      label: "Feature",
      description:
        "The locked feature the person asked for — a plan-locked surface in Fusion, or the MCP sign-in itself.",
      getValue: (row) => row.FeatureLabel,
      enableGrouping: true,
      filter: { type: "text" },
      renderCell({ row }) {
        return (
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="truncate">
              {row.original.FeatureName ?? row.original.FeatureCode}
            </span>
            {row.original.FeatureName && (
              <span className="truncate font-mono text-[10px] text-muted-foreground">
                {row.original.FeatureCode}
              </span>
            )}
          </span>
        )
      },
    },
    ReasonLabel: {
      id: "ReasonLabel",
      label: "Why",
      description:
        "What the entitlement rule answered: no license for the product, a license that is not live, a feature outside the packages, no named seat.",
      getValue: (row) => row.ReasonLabel,
      enableGrouping: true,
      filter: {
        type: "multi-select",
        placeholder: "Any reason",
        toolbar: true,
      },
      renderCell({ row }) {
        return (
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="truncate">{row.original.ReasonLabel}</span>
            <span className="truncate font-mono text-[10px] text-muted-foreground">
              {row.original.ReasonCode}
            </span>
          </span>
        )
      },
    },
    Attempts: {
      id: "Attempts",
      label: "Attempts",
      description:
        "Refused attempts that day. An AI client retries on its own, so MCP counts say how hard someone tried, not how many questions they asked.",
      getValue: (row) => row.Attempts,
      filter: { type: "number-range" },
      renderCell({ row }) {
        return (
          <span className="tabular-nums">
            {formatNumber(row.original.Attempts)}
          </span>
        )
      },
      summaryValue({ row }) {
        return row.original.Attempts
      },
    },
    LicenseStatusCode: {
      id: "LicenseStatusCode",
      label: "License now",
      description:
        "Where the customer stands today on the product that sells the feature — so a row reads refused on Friday, bridged on Monday. No license = the customer holds none for it.",
      getValue: (row) => row.LicenseStatusCode ?? LICENSE_STATUS_NONE,
      enableGrouping: true,
      renderCell({ row }) {
        return <LicenseNowCell row={row.original} />
      },
      exportValue({ row }) {
        return row.original.ProductName
          ? `${row.original.ProductName}: ${row.original.LicenseStatusCode ?? "none"}`
          : ""
      },
    },
    ProductName: {
      id: "ProductName",
      label: "Sold on",
      description: "The product whose plans sell the feature.",
      defaultHidden: true,
      getValue: (row) => row.ProductName ?? "",
      enableGrouping: true,
    },
    FeatureCode: {
      id: "FeatureCode",
      label: "Feature code",
      defaultHidden: true,
      getValue: (row) => row.FeatureCode,
    },
    ReasonCode: {
      id: "ReasonCode",
      label: "Reason code",
      defaultHidden: true,
      getValue: (row) => row.ReasonCode,
    },
    Email: {
      id: "Email",
      label: "Email",
      defaultHidden: true,
      getValue: (row) => row.Email ?? "",
    },
  },
})

// Newest day first, heaviest within the day: a hole in a customer's access
// shows on top the morning after the first refused sign-in.
const INITIAL_SORTING = [
  { id: "Date", desc: true },
  { id: "Attempts", desc: true },
]

const TABLE_ID = "license-denied-feed"

export type UsageLicenseDeniedFeedTableProps = Omit<
  TableProps<UsageLicenseDeniedFeedRow>,
  "data" | "columns"
>

// The refusals feed, cross-customer: every stored refusal row of the window
// (acc.LicenseDeniedDaily: one person × day × product × feature × reason),
// where the customer stands on the product today, and the people and
// customers behind the window on top. The same facts the customer panel's
// Activity tab and the Portfolio's demand section roll up — kept at the raw
// grain here so it can be watched as a feed, which is how Atlas Eiendom's
// weekend without MCP access was visible from Saturday morning (2 Nov 2026)
// had anyone been looking.
function UsageLicenseDeniedFeedTable(props: UsageLicenseDeniedFeedTableProps) {
  const { className, ...tableProps } = props
  const opener = useAppViewSheetOpener()
  const [windowDays, setWindowDays] = useState<LicenseDeniedFeedWindow>("28")

  const { data, previousData, loading, error, refetch } =
    useUsageLicenseDeniedFeedQuery({
      variables: { days: Number(windowDays) },
    })
  const events = (data ?? previousData)?.getUsageLicenseDeniedFeed

  const rows = useMemo<UsageLicenseDeniedFeedRow[]>(() => {
    return (events ?? []).map((event) => ({
      ...event,
      Id: `${event.UserId}|${event.Date}|${event.AppCode}|${event.FeatureCode}|${event.ReasonCode}`,
      UserName:
        event.FirstName || event.LastName
          ? formatUserName(event.FirstName, event.LastName)
          : (event.Email ?? `User #${event.UserId}`),
      ReasonLabel: formatLicenseDeniedReason(event.ReasonCode),
      FeatureLabel: event.FeatureName
        ? `${event.FeatureName} ${event.FeatureCode}`
        : event.FeatureCode,
    }))
  }, [events])

  const summary = useMemo(() => {
    const attempts = rows.reduce((sum, row) => sum + row.Attempts, 0)
    const people = new Set(rows.map((row) => row.UserId)).size
    const customers = new Set(rows.map((row) => row.CustomerId)).size
    const features = new Set(rows.map((row) => row.FeatureCode)).size
    // Customers whose license on the product is still not live today: the
    // rows that may still be a hole, not history.
    const stillOut = new Set(
      rows
        .filter(
          (row) =>
            row.LicenseStatusCode == null ||
            ![1, 2, 4].includes(row.LicenseStatusCode),
        )
        .map((row) => row.CustomerId),
    ).size
    return { attempts, people, customers, features, stillOut }
  }, [rows])

  const sortUrl = useTableSortUrl({
    tableId: TABLE_ID,
    initialSorting: INITIAL_SORTING,
  })

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <div className={usageKpiGridClass(5)}>
        <UsageReportKpi
          label="Refused attempts"
          value={formatNumber(summary.attempts)}
          sub={`in the last ${windowDays} days`}
          tone={summary.attempts > 0 ? "warning" : "default"}
        />
        <UsageReportKpi
          label="People"
          value={formatNumber(summary.people)}
          sub="refused at least once"
        />
        <UsageReportKpi
          label="Customers"
          value={formatNumber(summary.customers)}
          sub="with someone refused"
        />
        <UsageReportKpi
          label="Features"
          value={formatNumber(summary.features)}
          sub="asked for while locked"
        />
        <UsageReportKpi
          label="Still without"
          value={formatNumber(summary.stillOut)}
          sub="customers whose license on the product is not live today"
          tone={summary.stillOut > 0 ? "warning" : "default"}
        />
      </div>

      <Table<UsageLicenseDeniedFeedRow>
        className="license-denied-feed-table"
        tableId={TABLE_ID}
        data={rows}
        columns={COLUMNS}
        initialColumnSizing={COLUMN_SIZING}
        {...sortUrl}
        enableGrouping
        enableInfiniteScroll
        getRowId={(row) => row.Id}
        getHighlightedRowId={(row) =>
          opener.getIsActive("user", row.original.UserId) ||
          (!!row.original.ExternalCustomerId &&
            opener.getIsActive("customer", row.original.ExternalCustomerId))
        }
        {...tableProps}
      >
        <Table.Toolbar>
          <UsageLicenseDeniedWindowSelect
            value={windowDays}
            onChange={setWindowDays}
          />
          <div className="flex-1" />
          <Table.Search placeholder="Search customer, person, feature or reason…" />
          <Table.ToolbarFilters />
          <Separator orientation="vertical" />
          <Table.ToolbarMenu
            onRefresh={refetch}
            exportFileName="license-refusals"
          />
        </Table.Toolbar>

        <Table.FilterList />
        <Table.GroupList />

        <Table.Content
          countLabel="refusals"
          isLoading={loading && !events}
          error={error}
          renderEmpty={
            <div className="py-10 text-center text-sm text-muted-foreground">
              Nobody met a license lock in the last {windowDays} days. Rows
              appear the morning after someone clicks a locked feature or is
              refused by an MCP server.
            </div>
          }
        />
      </Table>
    </div>
  )
}

type LicenseNowCellProps = {
  row: UsageLicenseDeniedFeedRow
}

// The customer's standing today on the product that sells the feature: the
// governing pill (with the trial clock or the end stamp) and the product it
// is about. "—" when the catalog sells the feature nowhere.
function LicenseNowCell(props: LicenseNowCellProps) {
  const { row } = props
  const { namesByCode } = useLicenseGoverningStatuses()

  if (!row.ProductName) {
    return <span className="text-xs text-muted-foreground">—</span>
  }

  return (
    <span className="flex min-w-0 flex-col items-start gap-0.5 leading-tight">
      <LicenseGoverningBadge
        size="xs"
        license={{
          LicenseStatusCode: row.LicenseStatusCode ?? LICENSE_STATUS_NONE,
          LicenseTrialEndsAt: row.LicenseTrialEndsAt,
        }}
        statusName={
          row.LicenseStatusCode != null
            ? namesByCode.get(row.LicenseStatusCode)
            : undefined
        }
      />
      <span className="truncate text-[10px] text-muted-foreground">
        {row.ProductName}
      </span>
    </span>
  )
}

type UsageLicenseDeniedWindowSelectProps = {
  value: LicenseDeniedFeedWindow
  onChange(value: LicenseDeniedFeedWindow): void
}

// The window the feed reads, inline among the toolbar filters (the sign-in
// tabs' period control, in days because the store is daily).
function UsageLicenseDeniedWindowSelect(
  props: UsageLicenseDeniedWindowSelectProps,
) {
  const { value, onChange } = props

  return (
    <div className="ui-table-toolbar-filter">
      <label className="ui-table-toolbar-filter-label">
        <Tooltip>
          <Tooltip.Trigger asChild tabIndex={-1}>
            <span>Window</span>
          </Tooltip.Trigger>
          <Tooltip.Content
            side="top"
            className="ui-tooltip-content--panel max-w-[280px]"
          >
            How many days back the feed reads. The counts above cover the same
            window; the filters narrow within it.
          </Tooltip.Content>
        </Tooltip>
      </label>
      <Select
        value={value}
        onValueChange={(next) => onChange(next as LicenseDeniedFeedWindow)}
      >
        <Select.Trigger size="xs" className="w-28">
          <Select.Value />
        </Select.Trigger>
        <Select.Content>
          {LICENSE_DENIED_FEED_WINDOW_OPTIONS.map((option) => (
            <Select.Item key={option.value} value={option.value}>
              {option.label}
            </Select.Item>
          ))}
        </Select.Content>
      </Select>
    </div>
  )
}

export default UsageLicenseDeniedFeedTable
